import { json } from "@remix-run/node";
import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";

import FaqsSection from "~/components/landing-page/Sections/FaqsSection";
import Sections from "~/components/landing-page/Sections/Sections";
import { mapImage } from "~/components/landing-page/Sections/util/mapImage.server";
import { mapSections } from "~/components/landing-page/Sections/util/mapSections.server";
import type { Asset, Seo } from "~/generated/schema.server";
import { hygraphSdk } from "~/integrations/hygraph/client";
import { getStripeProducts } from "~/modules/stripe/stripe.server";
import { mapSubscription } from "~/modules/subscription/mapSubscription.server";
import { SupaStripeStackError } from "~/utils/error";
import { response } from "~/utils/http.server";
import mapSEO from "~/utils/mapSEO.server";

export type LandingPageLoader = typeof loader;

export const links: LinksFunction = () => [
  {
    rel: "stylesheet",
    href: "https://cdn.jsdelivr.net/npm/swiper@11.0.5/swiper-bundle.min.css",
  },
];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  try {
    const [products, { GetHomePage, GetPartners }] = await Promise.all([
      getStripeProducts("NZ"),
      hygraphSdk(),
    ]);

    const [{ page }, { partners }] = await Promise.all([
      GetHomePage(),
      GetPartners(),
    ]);

    const mappedProduct = mapSubscription(products);

    if (!page) {
      throw new SupaStripeStackError({
        message: "No page found",
      });
    }

    const mappedPage = {
      ...page,
      sections: mapSections(page.sections),
      seo: mapSEO(page.seo as Seo, page.slug || undefined, request.url),
    };

    return json({
      products: mappedProduct,
      page: mappedPage,
      partners: partners.map((partner) => ({
        id: partner.id,
        url: partner.url ?? undefined,
        logo: mapImage(partner.logo as Asset),
      })),
      faqs: page.faqs.map((faq) => ({
        id: faq.id,
        question: faq.question,
        answerRaw: faq.answerRaw?.raw ?? null,
      })),
    });
  } catch (cause) {
    throw response.error(cause, { authSession: null });
  }
};

export const meta: MetaFunction<LandingPageLoader> = ({ data }) => {
  if (!data?.page.seo) {
    return [{}];
  }

  return [data.page.seo];
};

export function headers() {
  return {
    "Cache-Control": `s-maxage=${60 * 60 * 24}, stale-while-revalidate=60`,
  };
}

const LandingPage = () => (
  <>
    <Sections />
    <FaqsSection />
  </>
);

export default LandingPage;
